/**
 * What a scanned or typed code means, and what to go and ask about it.
 *
 * <p>
 * <strong>A hardware barcode scanner is a keyboard.</strong> It types the payload and presses
 * Enter, so the whole mechanism is a text input and a submit — no camera, no permission prompt, no
 * library. That is why this module is pure text handling, and why §4 of
 * `docs/Counter_UX_Redesign.md` puts camera scanning in a later phase for phones: a counter does not
 * need it, and reaching for `BarcodeDetector` would make the common case depend on the rare one.
 * </p>
 *
 * <p>
 * <strong>It must not decide what a code is from the text alone, and this is the load-bearing
 * rule.</strong> Document-series prefixes, separators, zero padding and whether the financial year
 * appears at all are per tenant, per branch and per document type — `DocumentNumberingSeries`
 * carries all four as columns — so `BK-2026-27-0001` is one shop's shape and `INV0001` is another
 * legitimate one. Unit serials are no more distinguishable: `TUX_NAVY-40-NAVY-SLIM-001` has the same
 * hyphens. A classifier keyed on any of that would be right on the seed data and wrong at the first
 * customer who renamed a series.
 * </p>
 *
 * <p>
 * So: <strong>ask both, and let the answers decide.</strong> {@link readScan} normalises the input
 * and returns the lookups worth running, best guess first. The order is a <em>tie-break only</em> —
 * it settles which to open when both come back with exactly one hit, and settles nothing else. One
 * hit navigates, several offer a choice, none says so plainly.
 * </p>
 */

/** The two things a code can name. Both are already searchable server-side. */
export type LookupKind = 'booking' | 'unit';

export interface ScanLookup {
  kind: LookupKind;
  /** What to send as the `search` parameter. */
  term: string;
}

export interface ScanIntent {
  /** The input, cleaned up: the thing to show back to the person and to search on. */
  term: string;
  /**
   * The lookups worth running, best guess first — empty when there is nothing to search.
   *
   * <p>
   * Both kinds are almost always present. A lookup is dropped only when it <em>cannot</em> match: a
   * unit serial has no spaces in it, so "Hiren Patel" is not worth a round trip to the unit index.
   * </p>
   */
  lookups: ScanLookup[];
}

/**
 * The shortest input worth asking the server about.
 *
 * <p>
 * Two characters match a large fraction of a booking register and come back as a page of noise the
 * person then has to read. Three is the same floor the pickers use.
 * </p>
 */
export const minimumScanLength = 3;

/**
 * Cleans up what arrived and says what to look for.
 *
 * <p>
 * The cleaning is not cosmetic. A scanner appends its terminator and, depending on how it is
 * configured, a prefix character too; a person pasting a reference out of WhatsApp brings the
 * surrounding whitespace with them. `rail.ts` already trims scanner whitespace for the rail's
 * filter, with a test named for it — this is the same fact met again at the input.
 * </p>
 */
export function readScan(raw: string): ScanIntent {
  const term = normalizeScan(raw);

  if (term.length < minimumScanLength) {
    return { term, lookups: [] };
  }

  if (looksLikeUnit(term)) {
    return {
      term,
      lookups: [
        { kind: 'unit', term },
        { kind: 'booking', term },
      ],
    };
  }

  // A serial has no spaces in it, so a name — or a booking number typed with the wrong separator —
  // is worth one round trip rather than two.
  const lookups: ScanLookup[] = [{ kind: 'booking', term }];

  if (!term.includes(' ')) {
    lookups.push({ kind: 'unit', term });
  }

  return { term, lookups };
}

/** Zero-width spaces and bidi marks, which a copy-paste out of a PDF carries and nobody can see. */
const INVISIBLE = /[\u200B-\u200F\u2060\uFEFF]/g;

/**
 * Every control character, which is where a scanner's configured prefix and suffix land.
 *
 * <p>
 * `no-control-regex` is disabled deliberately, and only here. The rule exists to catch a control
 * character that reached a pattern by accident; this one is the pattern's whole subject — a scanner
 * is a keyboard that types its payload between an optional prefix byte and a terminator, and those
 * bytes are exactly what has to come off before the text is searched.
 * </p>
 */
// eslint-disable-next-line no-control-regex -- the control characters are the subject; see above.
const CONTROL = /[\u0000-\u001F\u007F]/g;

/**
 * Whitespace, terminators and the invisible characters a scanner or a paste can carry.
 *
 * <p>
 * Inner runs collapse to a single space rather than being stripped: "Hiren  Patel" is a name with a
 * double space in it, and joining that into "HirenPatel" would make it unfindable. The case is left
 * alone — SQL Server's collation folds it, and upper-casing here would print a customer's name back
 * at them in capitals.
 * </p>
 */
export function normalizeScan(raw: string): string {
  return raw.replace(INVISIBLE, '').replace(CONTROL, ' ').replace(/\s+/g, ' ').trim();
}

/**
 * A weak signal that this is a garment rather than a booking — and weak is the honest word.
 *
 * <p>
 * <strong>Only ever used to order the lookups, never to skip one.</strong> Serial codes are built
 * from the item code and the variant axes — `TUX_NAVY-38-NAVY-REGULAR-005` — so an underscore or
 * five hyphen-separated segments is a real hint; but a shop may set its item codes to anything, and
 * a numbering series may be configured into the same shape. Both lookups still run, so being wrong
 * here costs a tie-break and nothing else.
 * </p>
 *
 * <p>
 * <strong>Five, not four, and the off-by-one is the whole reason to check.</strong> A default
 * document number is `BK-2026-27-0001` — four segments, because the financial year contributes one
 * of them — so a threshold of four would put the most commonly scanned code in the product, the QR
 * on the customer's own copy, behind the unit index every time.
 * </p>
 */
export function looksLikeUnit(term: string): boolean {
  if (term.includes(' ')) {
    return false;
  }

  return term.includes('_') || term.split('-').length >= 5;
}

/** One garment, as much of it as matching needs. */
export interface ScannableGarment {
  serialCode: string;
  itemName: string;
}

export type ScopedScan<T extends ScannableGarment> =
  /** On this booking, and this is the row to tick. */
  | { outcome: 'matched'; garment: T }
  /**
   * On this booking, but not something this counter can act on.
   *
   * <p>
   * Distinct from {@link ScopedScan.elsewhere} because the two are different mistakes and want
   * different sentences. "Not on this booking" sends a clerk back to the rail; "on this booking but
   * still at the tailor" sends them to the tailor. Collapsing them would make the second read as
   * the first, which is a clerk hunting a garment that is exactly where the system says it is.
   * </p>
   */
  | { outcome: 'ineligible'; garment: T }
  /** A real code, but for something this booking is not for. */
  | { outcome: 'elsewhere'; term: string }
  /** Nothing typed, or nothing left after cleaning. */
  | { outcome: 'empty' };

/**
 * Matches a scan against the garments already on the booking in front of the clerk.
 *
 * <p>
 * <strong>Scoped, not a search.</strong> At the issue and return counters the question is not "what
 * is this" — the booking is already open — it is "is this one of these, and which row". So this
 * matches the whole serial exactly rather than searching, and <strong>a code that is not on this
 * booking is refused by name</strong>. That refusal is the point of the feature: it is how a clerk
 * catches themselves scanning the sherwani from the next booking on the rail, which is exactly the
 * mistake a paper checklist cannot catch — and the reason both counters have been headed "Scan each
 * garment out / in" since they were built, with nothing behind it.
 * </p>
 *
 * <p>
 * Case-insensitive, because a scanner reads what is printed and a keyboard does not always. Exact on
 * the whole code, because a prefix match would tick `…-001` when `…-0011` was scanned.
 * </p>
 */
export function scopedScan<T extends ScannableGarment>(
  raw: string,
  garments: T[],
  /**
   * Whether this counter can act on the garment. Everything on the booking is matched either way —
   * a garment the counter cannot take is a different answer from one it has never heard of.
   */
  eligible: (garment: T) => boolean = () => true,
): ScopedScan<T> {
  const term = normalizeScan(raw);

  if (!term) {
    return { outcome: 'empty' };
  }

  const wanted = term.toLowerCase();
  const garment = garments.find((candidate) => candidate.serialCode.toLowerCase() === wanted);

  if (!garment) {
    return { outcome: 'elsewhere', term };
  }

  return eligible(garment) ? { outcome: 'matched', garment } : { outcome: 'ineligible', garment };
}
